import { useEffect, useRef, useState, useMemo } from "react";
import { allowOrWarn, Util } from '../../util/Util';
import Invoices from "./Invoices";
import { useAuth } from "../../hooks/useAuth";
import { useData } from "../../hooks/useData";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import BulkPaymentModal from "../payments/BulkPaymentModal";
import { Box, Button, IconButton, ListItemIcon, MenuItem, Tooltip, Typography, useTheme } from '@mui/material';
import { LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../util/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TableView from "../../components/TableView";
import { AccountBalanceWallet, AttachMoney, FileDownload, Refresh } from "@mui/icons-material";
import { useDateRange, getPeriodQueryParams } from "../../hooks/useDateRange";
import SituationExportDialog, { PageOrientation, SituationDebtExportOptions } from "../../components/SituationExportDialog";
import { MRT_ColumnDef } from "material-react-table";
import {
	RECON_CHART_COLORS,
	ReconPhase,
	ReconRow,
	buildWaterfallSteps,
	CreditUsageCell,
	creditUsageTableCellProps,
	getChartSurface,
	isUnlimitedCredit,
	SituationBalanceFlowChart,
	SituationChartsGrid,
	SituationDetailHeader,
	SituationDetailShell,
	SituationPeriodAlert,
	SituationEndBalancesChart,
	SituationEntityComposedChart,
	SituationKpiGrid,
	SituationReconciliation,
} from './situationDetailShared';

import { roundQty } from "../../util/quantity";
import { canExportPdf, canRecord } from '../../util/access';
const hasClientPeriodActivity = (record: any) => [
	'period_client_invoiced', 'period_client_paid', 'period_client_balance',
].some((key) => Number(record[key] ?? 0) !== 0);

const ClientSituation = () => {
	const [toPay, setToPay] = useState<any>(null);
	const [toView, setToView] = useState<any>(null);
	const [activatingWallet, setActivatingWallet] = useState<number | null>(null);
	const [onExport, setOnExport] = useState<boolean>(false);
	const [exportSelectedIds, setExportSelectedIds] = useState<number[]>([]);

	const theme = useTheme();
	const { api, session, access } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { dateRange: period, setDateRange: setPeriod } = useDateRange(LocalStorageKeys.CLIENT_SIT_RECORDS);
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, loading, setLoading } = useData(LocalStorageKeys.CLIENT_SIT_RECORDS);

	const toastIdRef = useRef<string | number>(-1);
	const firstFetchRef = useRef(false);

	useEffect(() => {
		if (!loadFromIndexedDB() && !firstFetchRef.current) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, []);

	const fetchData = async (useLoadModal: boolean = true) => {

		setLoading(true);

		const res = await api.get("clients/situations", {
			params: {
				...getPeriodQueryParams(period),
			},
		});

		if (res) {
			if (res.status === 200) {
				saveFromServer(res.data, period);
				toast.update(toastIdRef.current, {
					render: t("common:toast.success.refresh"),
					type: "success",
					isLoading: false,
					autoClose: 3000,
				});
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
				toast.dismiss(toastIdRef.current);
			}
		} else {
			toast.dismiss(toastIdRef.current);
		}

		setLoading(false);
	};

	const openPdfBlob = (encoded: string) => {
		const pdfData = atob(encoded);
		const blob = new Blob([Uint8Array.from(pdfData, (c) => c.charCodeAt(0))], { type: "application/pdf" });
		window.open(URL.createObjectURL(blob));
	};

	const exportLedger = async (selectedIds: number[], orientation: PageOrientation = "landscape") => {
		if (!canExportPdf(access, "report")) return;
		setLoadingMessage("EXPORT");

		const payload: Record<string, unknown> = {
			...getPeriodQueryParams(period),
			orientation,
		};
		if (selectedIds.length > 0) {
			payload.Clients = selectedIds;
		}

		const res = await api.post("/reports/invoices/clients/ledger", payload);

		if (res?.status === 200) {
			openPdfBlob(res.data.data);
		} else if (res) {
			console.error("Failed to export ledger:", res);
		}

		setLoadingMessage(null);
	};

	const exportDebt = async (selectedIds: number[], options: SituationDebtExportOptions) => {
		setLoadingMessage("EXPORT");

		const payload: Record<string, unknown> = {
			...getPeriodQueryParams(period),
			includeZeroBalance: options.includeZeroBalance,
			includeSuspended: options.includeSuspended,
			comprehensive: options.comprehensive,
			fileType: options.fileType,
		};
		if (selectedIds.length > 0) {
			payload.Clients = selectedIds;
		}

		const res = await api.post("print/debts", payload);

		if (res?.status === 200) {
			if (options.fileType === "pdf") {
				openPdfBlob(res.data.data);
			} else {
				const blob = new Blob([res.data], { type: "text/csv" });
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				const { From, To } = getPeriodQueryParams(period);
				a.download = From && To ? `client_debts_${From}-${To}.csv` : `client_debts_current.csv`;
				document.body.appendChild(a);
				a.click();
				a.remove();
				window.URL.revokeObjectURL(url);
			}
		} else if (res) {
			console.error("Failed to export debt situation:", res);
		}

		setLoadingMessage(null);
	};

	const renderClientDetailPanel = ({ row }: { row: any }) => {
		const record = row.original;
		const chartSurface = getChartSurface(theme);
		const periodActive = hasClientPeriodActivity(record);
		const unlimited = isUnlimitedCredit(Number(record.credit_limit ?? 0));
		const phaseLabel = (phase: ReconPhase) => t(`common:clientSituation.detailPanel.phases.${phase}`);

		const clientRow: ReconRow = {
			label: 'Client',
			before: {
				invoiced: Number(record.before_client_invoiced ?? 0),
				paid: Number(record.before_client_paid ?? 0),
				balance: Number(record.before_client_balance ?? 0),
			},
			period: {
				invoiced: Number(record.period_client_invoiced ?? 0),
				paid: Number(record.period_client_paid ?? 0),
				balance: Number(record.period_client_balance ?? 0),
			},
			end: {
				invoiced: Number(record.after_client_invoiced ?? 0),
				paid: Number(record.after_client_paid ?? 0),
				balance: Number(record.after_client_balance ?? 0),
			},
		};

		const clientLabel = t('common:clientSituation.detailPanel.entities.client');
		const waterfallData = buildWaterfallSteps(clientRow, {
			balance: RECON_CHART_COLORS.balance,
			increase: chartSurface.debitBalance,
			decrease: chartSurface.creditBalance,
		}).map((step, index) => ({
			...step,
			name: (['before', 'period', 'end'] as ReconPhase[]).map(phaseLabel)[index],
		}));

		return (
			<SituationDetailShell theme={theme}>
				<SituationDetailHeader
					name={record.name}
					subtitle={`${t('common:clientSituation.detailPanel.client')}${record.is_agent ? ` · ${t('common:clientSituation.detailPanel.agent')}` : ''}`}
					isActive={Boolean(record.is_active)}
					unlimited={unlimited}
					creditLimit={record.credit_limit}
					t={t}
				/>

				<SituationPeriodAlert
					periodActive={periodActive}
					label={t('common:clientSituation.detailPanel.noPeriodActivity')}
				/>

				<SituationKpiGrid
					theme={theme}
					stats={[
						{ label: t('common:clientSituation.detailPanel.endBalance'), value: clientRow.end.balance, signed: true },
						{ label: t('common:clientSituation.detailPanel.endInvoiced'), value: clientRow.end.invoiced, signed: false },
						{ label: t('common:clientSituation.detailPanel.endPaid'), value: clientRow.end.paid, signed: false },
						{ label: t('common:clientSituation.detailPanel.periodChange'), value: clientRow.period.balance, signed: true },
					]}
				/>

				<SituationReconciliation
					rows={[clientRow]}
					periodActive={periodActive}
					getRowLabel={() => clientLabel}
					t={t}
					theme={theme}
				/>

				<SituationChartsGrid
					chartSurface={chartSurface}
					periodActive={periodActive}
					overviewTitle={t('common:clientSituation.detailPanel.charts.clientEndOverview')}
					overviewChart={(
						<SituationEntityComposedChart
							data={[{
								entity: clientLabel,
								invoiced: clientRow.end.invoiced,
								paid: clientRow.end.paid,
								balance: clientRow.end.balance,
							}]}
							t={t}
							theme={theme}
						/>
					)}
					periodTitle={t('common:clientSituation.detailPanel.charts.periodActivity')}
					periodChart={(
						<SituationEntityComposedChart
							data={[{
								entity: clientLabel,
								invoiced: clientRow.period.invoiced,
								paid: clientRow.period.paid,
								balance: clientRow.period.balance,
							}]}
							t={t}
							theme={theme}
						/>
					)}
					endBalanceTitle={t('common:clientSituation.detailPanel.charts.clientEndBalance')}
					endBalanceChart={(
						<SituationEndBalancesChart
							data={[{
								entity: clientLabel,
								balance: clientRow.end.balance,
								fill: clientRow.end.balance < 0 ? chartSurface.creditBalance : RECON_CHART_COLORS.client,
							}]}
							t={t}
							theme={theme}
						/>
					)}
					balanceFlowTitle={t('common:clientSituation.detailPanel.charts.clientBalanceFlow')}
					balanceFlowChart={(
						<SituationBalanceFlowChart data={waterfallData} t={t} theme={theme} />
					)}
				/>
			</SituationDetailShell>
		);
	};

	const canActivateWallet = canRecord(access, 'wallet');

	/** Create the client's wallet, or switch it back on. */
	const activateWallet = async (client: any) => {
		setActivatingWallet(client.id);
		const res = await api.post(`/clients/${client.id}/wallet`, {});
		setActivatingWallet(null);

		if (!res) return;
		if (res.status === 201 || res.status === 200) {
			toast.success(
				t(res.status === 201 ? "common:clientSituation.walletActivated" : "common:clientSituation.walletAlreadyActive"),
				{ autoClose: 3000 },
			);
			return;
		}
		if (res.status === 401 || res.status === 422) {
			queueReRun(() => activateWallet(client));
			return;
		}
		toast.error(res.data?.message || t("common:toast.error.integrity"), { autoClose: 3000 });
	};

	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.client"),
			accessorKey: "name",
			Cell: ({ cell }: { cell: any }) => (
				<button
					style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", textDecoration: "underline" }}
					onClick={() => setToView(cell.row.original)}
					disabled={Number(cell.row.original.after_client_balance ?? 0) === 0 || loading}
				>
					{cell.getValue()}
				</button>
			),
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			...creditUsageTableCellProps,
			header: t("common:clientSituation.creditUsage"),
			accessorKey: "after_client_balance",
			id: "credit_usage",
			Cell: ({ cell }: { cell: any }) => (
				<CreditUsageCell
					balance={Number(cell.row.original.after_client_balance ?? 0)}
					creditLimit={Number(cell.row.original.credit_limit ?? 0)}
					t={t}
					theme={theme}
				/>
			),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum',
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);

				useEffect(() => {
					setTotal(0);
					const rows = table.getFilteredRowModel().rows;
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.after_client_balance);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
					}, 0);

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			muiTableHeadCellProps: { align: 'center' },
			muiTableBodyCellProps: { align: 'center' },
			header: t("common:ui.labels.status"),
			accessorKey: "is_active",
			Cell: ({ cell }: { cell: any }) => {
				const isActive = Boolean(cell.getValue());
				return (
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
						<Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							{isActive && (
								<Box
									sx={{
										position: 'absolute',
										width: 10,
										height: 10,
										borderRadius: '50%',
										bgcolor: 'success.main',
										opacity: 0.4,
										animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite',
									}}
								/>
							)}
							<Box
								sx={{
									width: 10,
									height: 10,
									borderRadius: '50%',
									bgcolor: isActive ? 'success.main' : 'action.disabled',
									flexShrink: 0,
								}}
							/>
						</Box>
						<Typography
							variant="body2"
							sx={{
								fontWeight: 600,
								color: isActive ? 'success.main' : 'text.disabled',
								letterSpacing: 0.3,
							}}
						>
							{isActive ? t("common:status.active") : t("common:status.inactive")}
						</Typography>
						<style>{`@keyframes ping { 75%, 100% { transform: scale(2.5); opacity: 0; } }`}</style>
					</Box>
				);
			},
			filterFn: 'equals',
			filterVariant: 'checkbox',
		},
	], [loading, t, theme]);

	const handleChanges = () => {
		toastIdRef.current = toast.loading(`${t("common:toast.success.changes")} ${t("common:toast.loading.refresh")}`);
		fetchData(false);
	};

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
					stateKey={LocalStorageKeys.CLIENT_SIT_RECORDS}
					header={t("common:clientSituation.headers.clients")}
					data={data}
					columns={columns}
					includeTimestampColumns={false}
					totals={[{ key: "after_client_balance", label: t("common:tables.owing") }]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={setPeriod}
					isLoading={loading}
					renderDetailPanel={renderClientDetailPanel}

					renderTopToolbarActions={(table) => (
						<Box sx={{ display: 'flex', gap: '0.5rem' }}>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							<Button
								color="success"
								startIcon={<FileDownload />}
								variant="outlined"
								onClick={() => {
									setExportSelectedIds(
										table.getSelectedRowModel().rows.map((row) => row.original.id),
									);
									setOnExport(true);
								}}
							>
								{t("common:ui.buttons.export")}
							</Button>
						</Box>
					)}

					renderRowActionMenuItems={(row, closeMenu) => [
						canActivateWallet && (
							<MenuItem
								key="wallet"
								disabled={activatingWallet === row.original.id}
								onClick={() => {
									activateWallet(row.original);
									closeMenu();
								}}
								sx={{ color: 'info.main' }}
							>
								<ListItemIcon sx={{ color: 'info.main' }}>
									<AccountBalanceWallet />
								</ListItemIcon>
								{t("common:clientSituation.activateWallet")}
							</MenuItem>
						),
						Number(row.original.after_client_balance ?? 0) !== 0 && (
							<MenuItem
								key={0}
								disabled={toPay}
								onClick={() => {
									if (allowOrWarn(canRecord(access, 'payment') || canRecord(access, 'refund'))) {
										setToPay({ ...row.original, client: row.original.name });
									}
									closeMenu();
								}}
								sx={{
									color: 'success.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'success.main',
								}}>
									<AttachMoney />
								</ListItemIcon>
								{t("common:ui.buttons.pay")}
							</MenuItem>
						),
					]}
				/>

			</LocalizationProvider>

			{toView &&
				<Invoices
					title={t("common:clientSituation.unpaidInvoiceSituationClient", { name: toView.name })}
					isOpen={!!toView}
					toggle={() => setToView(null)}
					bundle={toView}
					onPayment={handleChanges}
				/>
			}

			{toPay && (
				<BulkPaymentModal
					isOpen={!!toPay}
					toggle={() => setToPay(null)}
					bundle={toPay}
					onSuccess={handleChanges}
				/>
			)}

			<SituationExportDialog
				open={onExport}
				onClose={() => setOnExport(false)}
				mode="client"
				period={period}
				selectedIds={exportSelectedIds}
				onExportLedger={exportLedger}
				onExportDebt={exportDebt}
			/>
		</>
	);
};

export default ClientSituation;